import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Wer wir sind', path: '/about' },
    { name: 'Was wir machen', path: '/achievements' },  
    { name: 'Wie du hilfst', path: '/help' }
  ];

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <motion.header 
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? 'bg-cream/95 backdrop-blur-md shadow-lg py-2 md:py-4' 
          : 'bg-transparent py-4 md:py-6'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('hero')}
            className="flex items-center space-x-3 group cursor-pointer"
          >
            <div className="w-16 h-16 bg-olive-green rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
              <Heart className="w-8 h-8 text-white fill-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-base font-bold text-charcoal font-heading">hay - humanity. action. you.</span>
              <span className="text-sm text-olive-green font-medium">Verein für humanitäre Hilfe & Solidarität.</span>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  if (item.path === '/') {
                    scrollToSection('hero');
                  } else if (item.path === '/about') {
                    scrollToSection('wer-wir-sind');
                  } else if (item.path === '/achievements') {
                    scrollToSection('achievements');
                  } else if (item.path === '/help') {
                    scrollToSection('donate');
                  }
                }}
                className="text-charcoal hover:text-olive-green transition-colors font-medium relative group"
              >
                {item.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-olive-green transition-all group-hover:w-full"></span>
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <LanguageSwitcher />
            <button
              onClick={() => scrollToSection('donate')}
              className="bg-olive-green hover:bg-olive-dark text-white px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Jetzt spenden
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-charcoal hover:text-olive-green transition-colors"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          className={`lg:hidden transition-all duration-300 ${
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          } overflow-hidden`}
          initial={false}
          animate={isMenuOpen ? 'open' : 'closed'}
        >
          <nav className="py-4 space-y-4 bg-cream/95 backdrop-blur-md rounded-lg mt-2 shadow-lg">
            <div className="px-6 pb-4">
              <LanguageSwitcher />
            </div>
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  if (item.path === '/') {
                    scrollToSection('hero');
                  } else if (item.path === '/about') {
                    scrollToSection('wer-wir-sind');
                  } else if (item.path === '/achievements') {
                    scrollToSection('achievements');
                  } else if (item.path === '/help') {
                    scrollToSection('donate');
                  }
                }}
                className="block w-full text-left px-6 py-3 text-charcoal hover:text-olive-green hover:bg-warm-beige transition-colors font-medium"
              >
                {item.name}
              </button>
            ))}
            <div className="px-6 pt-2">
              <button
                onClick={() => scrollToSection('donate')}
                className="w-full bg-olive-green hover:bg-olive-dark text-white px-6 py-3 rounded-full font-semibold transition-all"
              >
                Jetzt spenden
              </button>
            </div>
          </nav>
        </motion.div>
      </div>
    </motion.header>
  );
};

export default Header;